/** The web sidebar's takeoff tool rail: the shared TakeoffToolRail plus the three things only
 *  the DOM can add (Autodesk's icon font, the collapsed-rail tooltip, the `takeoff:tool`
 *  window event the canvas listens for). */
import React from 'react';
import type { ActiveTool } from '../../../types/takeoff';
import { TakeoffToolRail, type TakeoffRailMetrics } from '../../../shared-ui/takeoff/TakeoffToolRail';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../shared/ui/tooltip';
import {
  anchorRect, toolbarGroups, ToolIcon,
  type ToolbarContext, type ToolbarEntry,
} from './toolbarRegistry';

type TooltipProps = React.ComponentProps<typeof TooltipContent>;

export function TakeoffSidebarToolRail({ ctx, activeTool, collapsed, metrics, tooltipFor }: {
  ctx: ToolbarContext;
  activeTool: string;
  collapsed: boolean;
  metrics: TakeoffRailMetrics;
  /** The sidebar's collapsed-rail tooltip; undefined where hover does not exist. */
  tooltipFor: (label: string) => TooltipProps | undefined;
}) {
  // Rebuilt only when the registry context changes, so the rows are not re-created on every tool click.
  const groups = React.useMemo(() => toolbarGroups(ctx), [ctx]);
  const onSelect = React.useCallback((entry: ToolbarEntry, target: unknown) => {
    window.dispatchEvent(new CustomEvent('takeoff:tool', {
      detail: { id: entry.id, anchor: anchorRect(target as Element | null) },
    }));
  }, []);
  const renderGlyph = React.useCallback((entry: ToolbarEntry, colour: string, size: number) => {
    // Only the Autodesk surface swaps glyphs; the shared one is returned as null.
    if (!ctx.isApsViewer || !entry.apsIcon) return null;
    // The icon font sizes and colours itself from the surrounding text, so the rail's
    // metrics and palette colour travel as font-size and colour on a wrapper.
    return (
      <span style={{ color: colour, fontSize: size, display: 'inline-flex' }}>
        <ToolIcon entry={entry} ctx={ctx} className="size-[1em] text-[length:inherit]" />
      </span>
    );
  }, [ctx]);
  const wrapItem = React.useCallback((item: React.ReactElement, entry: ToolbarEntry) => {
    const label = entry.shortcut ? `${entry.label} (${entry.shortcut})` : entry.label;
    const tooltip = tooltipFor(label);
    if (!tooltip) return item;
    return (
      <Tooltip>
        <TooltipTrigger asChild><div>{item}</div></TooltipTrigger>
        <TooltipContent side="right" align="center" {...tooltip} />
      </Tooltip>
    );
  }, [tooltipFor]);

  return (
    <TakeoffToolRail
      groups={groups}
      ctx={ctx}
      activeTool={activeTool as ActiveTool}
      onSelect={onSelect}
      collapsed={collapsed}
      metrics={metrics}
      renderGlyph={renderGlyph}
      wrapItem={wrapItem}
    />
  );
}
